// 横屏锁定封装
// - Web 浏览器：尽力调用标准 Screen Orientation API，失败则静默跳过
// - iOS / Android Capacitor：优先使用运行时已注册的 ScreenOrientation 插件
// 这里不能 import @capacitor/*，否则服务器 web 构建必须安装移动壳依赖。

type CapacitorScreenOrientation = {
	lock?: (options: { orientation: 'landscape' | 'portrait' }) => Promise<void>;
	unlock?: () => Promise<void> | void;
};

type CapacitorGlobal = {
	isNative?: boolean;
	isNativePlatform?: () => boolean;
	getPlatform?: () => string;
	Plugins?: {
		ScreenOrientation?: CapacitorScreenOrientation;
	};
};

type LockableScreenOrientation = ScreenOrientation & {
	lock?: (orientation: 'landscape' | 'portrait') => Promise<void>;
	unlock?: () => void;
};

function getCapacitor(): CapacitorGlobal | undefined {
	if (typeof window === 'undefined') return undefined;
	return (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor;
}

function isNativeCapacitor(): boolean {
	const cap = getCapacitor();
	if (!cap) return false;
	if (typeof cap.isNativePlatform === 'function') return cap.isNativePlatform();
	if (typeof cap.getPlatform === 'function') return cap.getPlatform() !== 'web';
	return Boolean(cap.isNative);
}

function getNativeOrientationPlugin(): CapacitorScreenOrientation | undefined {
	if (!isNativeCapacitor()) return undefined;
	return getCapacitor()?.Plugins?.ScreenOrientation;
}

export async function lockLandscape(): Promise<void> {
	try {
		const nativePlugin = getNativeOrientationPlugin();
		if (nativePlugin?.lock) {
			await nativePlugin.lock({ orientation: 'landscape' });
			return;
		}
		const orientation = screen.orientation as LockableScreenOrientation | undefined;
		await orientation?.lock?.('landscape');
	} catch {
		/* 静默：插件未注册 / 浏览器不允许锁屏 / 平台不支持 */
	}
}

export async function lockPortrait(): Promise<void> {
	try {
		const nativePlugin = getNativeOrientationPlugin();
		if (nativePlugin?.lock) {
			await nativePlugin.lock({ orientation: 'portrait' });
			return;
		}
		const orientation = screen.orientation as LockableScreenOrientation | undefined;
		await orientation?.lock?.('portrait');
	} catch {
		/* 静默：插件未注册 / 浏览器不允许锁屏 / 平台不支持 */
	}
}

export async function unlockOrientation(): Promise<void> {
	try {
		const nativePlugin = getNativeOrientationPlugin();
		if (nativePlugin?.unlock) {
			await nativePlugin.unlock();
			return;
		}
		const orientation = screen.orientation as LockableScreenOrientation | undefined;
		orientation?.unlock?.();
	} catch {
		/* 静默 */
	}
}
